// The hub's list (#1854): the core and the chapters that are built, each with
// its status and the button that runs it. Live wherever it is shown — on the
// hub page, and in the closing cards — so a chapter starts from either.
// Chapters stay locked until the core is done; a finished unit offers
// «Пройти снова», which records nothing new.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserQuery } from '@/api/queries/useAuth';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { useUserTutorial } from '@/data/entities/userTutorial';
import type { TutorialUnit } from '@/types';
import { TourButton } from './TutorialOverlay';
import { type HubEntry, type HubStatus, hubEntries } from './tutorialUnits';
import { useOpenTutorialProject } from './useOpenTutorialProject';
import { useTutorialAccess } from './useTutorialAccess';

const STATUS_KEY: Record<HubStatus, string | null> = {
    new: null,
    done: 'tutorial.hub.status-done',
    skipped: 'tutorial.hub.status-skipped',
    locked: 'tutorial.hub.status-locked',
};

interface TutorialHubListProps {
    /** `page`: the hub screen's rows. `card`: compact, on an always-dark card. */
    surface: 'page' | 'card';
}

export function TutorialHubList({ surface }: TutorialHubListProps) {
    const { t } = useTranslation();
    const { data: user } = useUserQuery();
    const { data: tutorial } = useUserTutorial({ userId: user?.id ?? 0 }, { enabled: user !== undefined });
    // Notes stays hidden while the access loads.
    const access = useTutorialAccess(tutorial) ?? { canAuthorNotes: false };
    const { open, isOpening, ready } = useOpenTutorialProject();
    /** The unit whose button was pressed: only it spins. */
    const [starting, setStarting] = useState<TutorialUnit | null>(null);

    if (tutorial === undefined) return null;

    const start = (unit: TutorialUnit) => {
        if (!ready || isOpening) return;
        setStarting(unit);
        void open(unit).finally(() => setStarting(null));
    };
    const rowProps = (entry: HubEntry): RowProps => {
        const statusKey = STATUS_KEY[entry.status];
        return {
            entry,
            status: statusKey === null ? null : t(statusKey),
            action: entry.status === 'locked' ? null : t(entry.status === 'done' ? 'tutorial.hub.replay' : 'tutorial.hub.start'),
            busy: isOpening && starting === entry.unit,
            disabled: !ready || isOpening,
            onStart: () => start(entry.unit),
        };
    };

    const entries = hubEntries(tutorial, access);
    if (surface === 'card') {
        return (
            <ul data-testid="tutorial-hub" className="flex flex-col gap-1 text-sm">
                {entries.map(entry => (
                    <CardRow key={entry.unit} {...rowProps(entry)} />
                ))}
            </ul>
        );
    }
    const [core, ...chapters] = entries;
    return (
        <div data-testid="tutorial-hub" className="flex flex-col gap-4">
            {core && <PageRow {...rowProps(core)} />}
            {chapters.length > 0 && (
                <Text variant="body1" className="mt-2 font-medium">
                    {t('tutorial.hub.chapters')}
                </Text>
            )}
            {chapters.map(entry => (
                <PageRow key={entry.unit} {...rowProps(entry)} />
            ))}
        </div>
    );
}

interface RowProps {
    entry: HubEntry;
    status: string | null;
    /** The button's label; `null` while the unit is locked. */
    action: string | null;
    busy: boolean;
    disabled: boolean;
    onStart: () => void;
}

function PageRow({ entry, status, action, busy, disabled, onStart }: RowProps) {
    const { t } = useTranslation();
    const { unit } = entry;
    return (
        <section
            data-testid={`tutorial-hub-${unit}`}
            data-status={entry.status}
            className="flex items-center justify-between gap-6 rounded-2xl bg-bg-paper px-5 py-4 shadow-sm dark:bg-c24"
        >
            <div className="flex min-w-0 flex-col gap-1">
                <div className="flex items-baseline gap-3">
                    <Text variant="body1" className="font-medium">
                        {t(`tutorial.units.${unit}.title`)}
                    </Text>
                    <Text variant="caption" color="text-c45">
                        {t(`tutorial.units.${unit}.duration`)}
                    </Text>
                    {status && (
                        <Text variant="caption" color="text-c45" data-testid={`tutorial-hub-${unit}-status`}>
                            {status}
                        </Text>
                    )}
                </div>
                <Text variant="body2" color="text-c45">
                    {t(`tutorial.units.${unit}.description`)}
                </Text>
            </div>
            {action && (
                <Button
                    variant={entry.status === 'done' ? 'outlined' : 'contained'}
                    data-testid={`tutorial-hub-${unit}-start`}
                    onClick={onStart}
                    disabled={disabled}
                    startIcon={busy ? <Spinner size={16} /> : undefined}
                    className="shrink-0"
                >
                    {busy ? t('tutorial.hub.preparing') : action}
                </Button>
            )}
        </section>
    );
}

function CardRow({ entry, status, action, busy, onStart }: RowProps) {
    const { t } = useTranslation();
    const { unit } = entry;
    return (
        <li data-testid={`tutorial-hub-${unit}`} data-status={entry.status} className="flex items-center justify-between gap-3">
            <span className={entry.status === 'locked' ? 'opacity-60' : undefined}>{t(`tutorial.units.${unit}.title`)}</span>
            <span className="-my-1 flex shrink-0 items-center gap-1 text-xs">
                {status && <span className="opacity-50">{status}</span>}
                {action && (
                    <TourButton testId={`tutorial-hub-${unit}-start`} primary={entry.status !== 'done'} onClick={onStart}>
                        {busy ? <Spinner size={14} /> : action}
                    </TourButton>
                )}
            </span>
        </li>
    );
}
